import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { updateEloFromMatch } from '../components/services/eloService';

const AdminEncodeScoresLeague = () => {
  const { id: leagueId } = useParams();
  const [matches, setMatches] = useState([]);
  const [eloByPhone, setEloByPhone] = useState({});
  const [eloDeltasByMatch, setEloDeltasByMatch] = useState({});
  const [selectedMatches, setSelectedMatches] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMatchesAndPlayers = async () => {
      const snap = await getDocs(collection(db, `league_matches/${leagueId}/matches`));
      const allMatches = [];
      const phoneSet = new Set();

      snap.forEach(docSnap => {
        const data = docSnap.data();
        const sets = data.sets || [];
        const teamAScore = sets.filter(s => (s.teamAScore ?? 0) > (s.teamBScore ?? 0)).length;
        const teamBScore = sets.filter(s => (s.teamBScore ?? 0) > (s.teamAScore ?? 0)).length;

        const match = {
          ...data,
          id: docSnap.id,
          path: 'matches',
          teamA: { name: data.teamA, score: teamAScore },
          teamB: { name: data.teamB, score: teamBScore },
          teamAPlayers: [data.joueur1A, data.joueur2A].filter(Boolean),
          teamBPlayers: [data.joueur1B, data.joueur2B].filter(Boolean),
        };

        allMatches.push(match);
        [...match.teamAPlayers, ...match.teamBPlayers].forEach(p => p?.phone && phoneSet.add(p.phone));
      });

      const phoneToElo = {};
      for (const phone of phoneSet) {
        const q = query(collection(db, 'joueurs'), where('phone', '==', phone));
        const snap = await getDocs(q);
        if (!snap.empty) {
          phoneToElo[phone] = snap.docs[0].data().elo ?? 700;
        }
      }

      allMatches.sort((a, b) => new Date(a.date) - new Date(b.date));
      setMatches(allMatches);
      setEloByPhone(phoneToElo);
      setLoading(false);
    };

    fetchMatchesAndPlayers();
  }, [leagueId]);

  const handleForceEloUpdate = async (match) => {
    try {
      const deltas = await updateEloFromMatch(match, leagueId);
      if (!deltas) return;
      // 🔄 Mise à jour en live du ELO dans le state
      setEloByPhone(prev => {
        const updatedElo = { ...prev };
        for (const phone in deltas) {
          if (phone && typeof deltas[phone] === 'number') {
            updatedElo[phone] = (updatedElo[phone] ?? 700) + deltas[phone];
          }
        }
        return updatedElo;
      });

      setMatches(prev => prev.map(m => m.id === match.id ? { ...m, eloUpdated: true } : m));
    } catch (err) {
      console.error("Erreur ELO forcée:", err);
    }
  };

  const handleResetEloFlag = async (match) => {
    try {
      const ref = doc(db, `league_matches/${leagueId}/matches/${match.id}`);
      await updateDoc(ref, { eloUpdated: false });
      setMatches(prev => prev.map(m => m.id === match.id ? { ...m, eloUpdated: false } : m));
    } catch (err) {
      console.error("Erreur reset eloUpdated:", err);
    }
  };

  const handleLockToggle = async (matchId, isLocked) => {
    try {
      const matchRef = doc(db, `league_matches/${leagueId}/matches`, matchId);
      await updateDoc(matchRef, { locked: !isLocked });
      setMatches(prev =>
        prev.map(match =>
          match.id === matchId ? { ...match, locked: !isLocked } : match
        )
      );
    } catch (error) {
      console.error('Erreur lors de la mise à jour du verrouillage :', error);
    }
  };

  const handleUpdateAllElo = async () => {
    const updated = [];
  
    for (const match of matches) {
      const isPlayed = match.sets?.some(set => {
        const a = Number(set.teamAScore ?? 0);
        const b = Number(set.teamBScore ?? 0);
        return a > 0 || b > 0;
      });
  
      const eloAlreadyUpdated = match.eloUpdated === true;
  
      console.log(`🔎 Match ${match.id}: isPlayed=${isPlayed}, eloUpdated=${match.eloUpdated}`);
  
      if (!isPlayed || eloAlreadyUpdated) {
        console.log(`⏭️ Skip match ${match.id}`);
        continue;
      }
  
      try {
        const deltas = await updateEloFromMatch(match, leagueId);
        if (!deltas) continue;
  
        updated.push(match.id);
        // 🔄 Mise à jour en live du ELO dans le state
        setEloByPhone(prev => {
          const updatedElo = { ...prev };
          for (const phone in deltas) {
            if (phone && typeof deltas[phone] === 'number') {
              updatedElo[phone] = (updatedElo[phone] ?? 700) + deltas[phone];
            }
          }
          return updatedElo;
        });

        setMatches(prev =>
          prev.map(m => m.id === match.id ? { ...m, eloUpdated: true } : m)
        );
        console.log(`✅ ELO updated for match ${match.id}`);
      } catch (err) {
        console.error(`❌ Erreur pour match ${match.id}`, err);
      }
    }
  
    alert(`✅ ${updated.length} match(s) ont été mis à jour.`);
  };
  

  const handleUpdateSelectedElo = async () => {
    const updated = [];
  
    for (const match of matches) {
      const isSelected = selectedMatches[match.id];
      const isPlayed = match.sets?.some(set => {
        const a = Number(set.teamAScore ?? 0);
        const b = Number(set.teamBScore ?? 0);
        return a > 0 || b > 0;
      });
  
      const eloAlreadyUpdated = match.eloUpdated === true;
  
      console.log(`🔍 [Match ${match.id}] selected=${isSelected}, played=${isPlayed}, eloUpdated=${eloAlreadyUpdated}`);
  
      if (!isSelected || !isPlayed || eloAlreadyUpdated) {
        console.log(`⏭️ Skip match ${match.id}`);
        continue;
      }
  
      try {
        const deltas = await updateEloFromMatch(match, leagueId);
        setEloByPhone(prev => {
          const updatedElo = { ...prev };
          for (const phone in deltas) {
            if (phone && typeof deltas[phone] === 'number') {
              updatedElo[phone] = (updatedElo[phone] ?? 700) + deltas[phone];
            }
          }
          return updatedElo;
        });
        
        if (!deltas) continue;
  
        updated.push(match.id);
        setEloDeltasByMatch(prev => ({ ...prev, [match.id]: deltas }));
        setMatches(prev =>
          prev.map(m => m.id === match.id ? { ...m, eloUpdated: true } : m)
        );
        console.log(`✅ ELO updated for match ${match.id}`);
      } catch (err) {
        console.error(`❌ Erreur pour match ${match.id}`, err);
      }
    }
  
    alert(`✅ ${updated.length} match(s) sélectionné(s) ont été mis à jour.`);
  };
  

  if (loading) return <div className="text-center py-10">Chargement...</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h2 className="text-3xl font-bold text-center text-[#4c2f8e] mb-8">🛠 Admin Ligue {leagueId}</h2>

      <ul className="space-y-6 mb-12">
        {matches.map((match, idx) => (
          <li key={idx} className="p-6 bg-white rounded-lg shadow border">
            <div className="flex justify-between items-center mb-2">
              <div>
                <div className="text-lg font-semibold">{match.teamA.name} vs {match.teamB.name}</div>
                <div className="text-sm text-gray-600">
                  {match.sets?.map((set, i) => (
                    <div key={i}>Set {i + 1} : {set.teamAScore ?? 0} - {set.teamBScore ?? 0}</div>
                  ))}
                </div>
                <div className="text-sm text-gray-500 mt-1">Statut : {match.locked ? 'Verrouillé' : 'Déverrouillé'}</div>
              </div>
              <button
                onClick={() => handleLockToggle(match.id, match.locked)}
                className={`px-4 py-1 ${match.locked ? 'bg-red-600' : 'bg-green-600'} text-white rounded-full shadow hover:shadow-md`}
              >
                {match.locked ? 'Déverrouiller' : 'Verrouiller'}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <h3 className="text-2xl font-bold text-center text-[#4c2f8e] mb-4">⚙️ Gestion des ELO</h3>

      <div className="flex justify-center gap-4 flex-wrap mb-6">
        <button onClick={handleUpdateAllElo} className="px-6 py-3 bg-green-600 text-white rounded-full shadow hover:shadow-lg">✅ Tous les matchs</button>
        <button onClick={handleUpdateSelectedElo} className="px-6 py-3 bg-blue-600 text-white rounded-full shadow hover:shadow-lg">🧮 Sélectionnés</button>
        <button
          onClick={() => {
            const allSelected = Object.keys(selectedMatches).length === matches.length;
            const newSelection = {};
            if (!allSelected) matches.forEach(m => newSelection[m.id] = true);
            setSelectedMatches(allSelected ? {} : newSelection);
          }}
          className="px-6 py-2 bg-gray-300 text-gray-800 rounded shadow hover:bg-gray-400"
        >
          🎯 {Object.keys(selectedMatches).length === matches.length ? "Tout désélectionner" : "Tout sélectionner"}
        </button>
      </div>

      <ul className="space-y-6">
        {matches.map((match) => (
          <li key={match.id} className="p-6 bg-white rounded-lg shadow border">
            <div className="flex items-center space-x-2 mb-2">
              <input
                type="checkbox"
                checked={!!selectedMatches[match.id]}
                onChange={() =>
                  setSelectedMatches(prev => ({
                    ...prev,
                    [match.id]: !prev[match.id],
                  }))
                }
              />
              <span className="text-sm text-gray-500">Sélectionner ce match</span>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <div className="text-lg font-semibold">{match.teamA.name} vs {match.teamB.name}</div>
                <div className="text-sm text-gray-600">
                  {match.sets?.map((set, i) => (
                    <div key={i}>Set {i + 1} : {set.teamAScore ?? 0} - {set.teamBScore ?? 0}</div>
                  ))}
                </div>
                <div className="text-sm mt-1">
                  {match.eloUpdated ? (
                    <span className="text-green-600 font-semibold">ELO mis à jour ✅</span>
                  ) : (
                    <span className="text-red-600 font-semibold">ELO non mis à jour ❌</span>
                  )}
                </div>
                {[...(match.teamAPlayers || []), ...(match.teamBPlayers || [])].map((player, i) => {
                  const elo = eloByPhone[player.phone] ?? '...';
                  const delta = eloDeltasByMatch[match.id]?.[player.phone];
                  return (
                    <div key={i} className="text-sm text-gray-700">
                      {player.prenom} {player.nom} ({player.phone}) : ELO {elo}
                      {typeof delta === 'number' && (
                        <span className={delta >= 0 ? 'text-green-600' : 'text-red-500'}>
                          {' '}→ {elo + delta} ({delta >= 0 ? '+' : ''}{delta})
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-col space-y-2">
                <button
                  onClick={() => handleForceEloUpdate(match)}
                  className="px-4 py-1 bg-blue-600 text-white rounded shadow hover:bg-blue-700"
                >🔁 Forcer ELO</button>
                <button
                  onClick={() => handleResetEloFlag(match)}
                  className="px-4 py-1 bg-yellow-500 text-white rounded shadow hover:bg-yellow-600"
                >♻️ Reset Flag</button>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AdminEncodeScoresLeague;
